import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    useTheme,
    Badge,
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RateReviewIcon from '@mui/icons-material/RateReview';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useThemeMode } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useMenuContext } from '../context/MenuContext';
import UserProfileMenu from './UserProfileMenu';
import jalaali from 'jalaali-js';
import api from '../config/axiosConfig';
import usePolling from '../hooks/usePolling';
import { API } from '../utils/apiRoutes';
import { toPersianDigits } from '../utils/convertNumbers';
import PersianDayOfWeek from '../utils/PersianDayOfWeek';

// How often the header checks for unanswered support activity.
const SUPPORT_BADGE_POLL_MS = 45000;

// Assuming `user` could be something like { name: "John Doe" } or null
export default function Header({ onMobileToggle, mobileOpen }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [supportAnchor, setSupportAnchor] = useState(null);
    const supportOpen = Boolean(supportAnchor);
    const theme = useTheme();
    const { user, isLoading } = useAuth();
    const { selectedMenuItem, setSelectedMenuItem } = useMenuContext();
    const navigate = useNavigate();

    // console.log('anchor: ', anchorEl)
    // Handling menu
    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (!isLoading && !user) {
            navigate('/login');
        }
    }, [isLoading, user, navigate]);

    // Calculate Jalali date
    const currentDate = new Date();
    const jalaliDate = jalaali.toJalaali(currentDate);
    const formattedJalaliDate = `${jalaliDate.jy}/${jalaliDate.jm}/${jalaliDate.jd}`;

    const staffSupportRole = String(user?.staff_role?.name || '').toLowerCase();
    // Askers: institute staff (manager/secretary/accountant). Borna support agents
    // and superusers answer escalations.
    const canAskSupport =
        user?.is_superuser ||
        ['manager', 'secretary', 'accountant'].includes(staffSupportRole);
    const isBornaSupport = user?.is_superuser || user?.is_support_staff;

    // Unanswered-activity badge: unread replies for askers, open escalations
    // for support agents. Best-effort — errors just leave the badge as-is.
    const [supportBadge, setSupportBadge] = useState({ unread: 0, escalated: 0 });
    const fetchSupportBadge = useCallback(async () => {
        if (!user) return;
        try {
            let unread = 0;
            let escalated = 0;
            if (canAskSupport) {
                const res = await api.get(API.aiConversations);
                const items = res.data?.results || res.data || [];
                unread = items.reduce((sum, c) => sum + (c.unread_count || 0), 0);
            }
            if (isBornaSupport) {
                const res = await api.get(API.aiStaffConversations, {
                    params: { status: 'escalated' },
                });
                escalated = res.data?.count ?? (res.data?.results || []).length;
            }
            setSupportBadge({ unread, escalated });
        } catch {
            // keep previous badge value
        }
    }, [user, canAskSupport, isBornaSupport]);

    useEffect(() => {
        fetchSupportBadge();
        // The support screens fire this after a conversation is opened (read)
        // or resolved, so the badge clears immediately instead of on next poll.
        window.addEventListener('support-badge-sync', fetchSupportBadge);
        return () => window.removeEventListener('support-badge-sync', fetchSupportBadge);
    }, [fetchSupportBadge]);
    usePolling(fetchSupportBadge, SUPPORT_BADGE_POLL_MS, Boolean(user && (canAskSupport || isBornaSupport)));

    const supportBadgeTotal = supportBadge.unread + supportBadge.escalated;

    const supportItems = [
        {
            id: 'support-chat',
            label: 'پشتیبان هوشمند',
            description: 'گفتگو با پشتیبانی برنا',
            icon: <SmartToyOutlinedIcon fontSize="small" />,
            show: canAskSupport,
            badge: supportBadge.unread,
        },
        {
            id: 'staff-support-panel',
            label: 'گفتگوهای پشتیبانی',
            description: 'پاسخ به سوالات کاربران',
            icon: <SupportAgentIcon fontSize="small" />,
            show: isBornaSupport,
            badge: supportBadge.escalated,
        },
    ].filter((item) => item.show);

    const isSupportActive = supportItems.some((item) => item.id === selectedMenuItem);

    const handleSupportMenu = (event) => {
        setSupportAnchor(event.currentTarget);
    };

    const handleSupportClose = () => {
        setSupportAnchor(null);
    };

    const handleSupportSelect = (id) => {
        setSelectedMenuItem(id);
        setSupportAnchor(null);
    };

    const getActionButtonStyles = (isActive) => ({
        width: 40,
        height: 40,
        borderRadius: '0.6rem',
        color: isActive ? theme.palette.text.active : theme.palette.text.default,
        bgcolor: isActive ? theme.palette.button.active : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
            bgcolor: theme.palette.button.active,
            color: theme.palette.text.active,
        },
    });

    return (
        <AppBar position="fixed"
            sx={{
                zIndex: theme.zIndex.drawer + 1,
                bgcolor: theme.palette.background.chrome,
                boxShadow: 'none',
                height: '4rem',
                backgroundImage: 'none',
                borderRadius: 0,
                border: 'none',
                borderBottom: `1px solid ${theme.palette.border.main}`,
                // Sidebar is full-height on the right (desktop); keep the header
                // beside it (not over it) so the brand sits at the very top.
                width: { xs: '100%', sm: 'calc(100% - 260px)' },
                left: 0,
                right: 'auto',
                // Vertically center the toolbar block within the fixed header
                // height so its contents never spill past the bottom border.
                display: 'flex',
                justifyContent: 'center',
            }}>
            <Toolbar sx={{ position: 'relative', minHeight: 0, display: 'flex', flexDirection: 'row', direction: 'ltr', alignItems: 'center', gap: 2, pl: { xs: 2, sm: 1.5 }, pr: { xs: 2, sm: 1.5 } }}>
                <UserProfileMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} handleMenu={handleMenu} handleClose={handleClose} open={open}/>

                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                    <Tooltip title="یادداشت شخصی" arrow>
                        <IconButton
                            sx={getActionButtonStyles(selectedMenuItem === 'personal-notes')}
                            onClick={() => setSelectedMenuItem('personal-notes')}
                            aria-label="یادداشت شخصی"
                        >
                            <RateReviewIcon sx={{ fontSize: '1.4rem' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="یادداشت برای همکاران" arrow>
                        <IconButton
                            sx={getActionButtonStyles(selectedMenuItem === 'colleague-notes')}
                            onClick={() => setSelectedMenuItem('colleague-notes')}
                            aria-label="یادداشت برای همکاران"
                        >
                            <QuestionAnswerIcon sx={{ fontSize: '1.4rem' }} />
                        </IconButton>
                    </Tooltip>
                    {supportItems.length === 1 && (
                        <Tooltip title="پشتیبانی" arrow>
                            <IconButton
                                sx={getActionButtonStyles(isSupportActive)}
                                onClick={() => handleSupportSelect(supportItems[0].id)}
                                aria-label="پشتیبانی"
                            >
                                <Badge
                                    badgeContent={supportBadgeTotal > 0 ? toPersianDigits(String(supportBadgeTotal)) : 0}
                                    color="error"
                                >
                                    <SupportAgentIcon sx={{ fontSize: '1.4rem' }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}
                    {supportItems.length > 1 && (
                        <>
                            <Tooltip title="پشتیبانی" arrow>
                                <IconButton
                                    sx={getActionButtonStyles(isSupportActive)}
                                    onClick={handleSupportMenu}
                                    aria-haspopup="true"
                                    aria-expanded={supportOpen ? 'true' : undefined}
                                    aria-label="پشتیبانی"
                                >
                                    <Badge
                                        badgeContent={supportBadgeTotal > 0 ? toPersianDigits(String(supportBadgeTotal)) : 0}
                                        color="error"
                                    >
                                        <SupportAgentIcon sx={{ fontSize: '1.4rem' }} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Menu
                                anchorEl={supportAnchor}
                                open={supportOpen}
                                onClose={handleSupportClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                slotProps={{ paper: { sx: { minWidth: 260, mt: 1 } } }}
                            >
                                {supportItems.map((item) => (
                                    <MenuItem
                                        key={item.id}
                                        selected={selectedMenuItem === item.id}
                                        onClick={() => handleSupportSelect(item.id)}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit' }}>
                                            <Badge
                                                badgeContent={item.badge > 0 ? toPersianDigits(String(item.badge)) : 0}
                                                color="error"
                                            >
                                                {item.icon}
                                            </Badge>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            secondary={item.description}
                                        />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>

                <Typography sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 1,
                    direction: 'rtl',
                    fontSize: '0.875rem',
                    fontWeight: 'normal',
                    color: theme.palette.text.default,
                    whiteSpace: 'nowrap',
                }}>
                    <PersianDayOfWeek />
                    {toPersianDigits(formattedJalaliDate)}
                </Typography>

                {user?.institute_name && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '60%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 0.75,
                            direction: 'rtl',
                            pointerEvents: 'none',
                        }}
                    >
                        <ApartmentOutlinedIcon sx={{ fontSize: '1.2rem', color: theme.palette.text.secondary }} />
                        <Typography
                            sx={{
                                m: 0,
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: theme.palette.text.default,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            آموزشگاه {user.institute_name}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <IconButton
                    color="inherit"
                    aria-label={mobileOpen ? 'بستن منو' : 'باز کردن منو'}
                    aria-expanded={Boolean(mobileOpen)}
                    className="navbar-toggle-icon"
                    sx={{
                        display: { sm: 'none' },
                        bgcolor: theme.palette.button.active,
                        borderRadius: '.5rem',
                        '&:hover': {
                            bgcolor: theme.palette.button.deactive,
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.background.default,
                            }
                        },
                    }}
                    onClick={onMobileToggle}
                >
                    <MenuIcon />
                </IconButton>
            </Toolbar>
        </AppBar>
    );
}
